import React, { useCallback, useEffect, useRef, useState } from 'react';
import { parseISO } from 'date-fns';
import {
	Clock,
	Download,
	HelpCircle,
	Hourglass,
	MessageCircle,
	PlayCircle,
	RotateCcw,
	Search,
	Send,
	Upload,
	XCircle,
} from 'lucide-react';
import { BACKEND_URL } from '../../config';
import { formatCustomDistanceToNow } from '../../utils/timeUtils';
import ForwardReportModal from '../ForwardReportModal/ForwardReportModal';
import KanbanGuide, { hasSeenKanbanGuide, markKanbanGuideSeen } from './KanbanGuide';
import {
	AUTHORITY_TABS,
	EMPTY_COUNTS,
	ReportNumberChip,
	ReportTab,
	TAB_LABELS,
	TabCounts,
	buildReportsUrl,
	canActivate,
	canCloseFrom,
	canForwardFrom,
	countForTab,
	fetchTabCounts,
	isCardDraggable,
	isDropAllowed,
	renderHighlightedText,
} from './reportShared';

/**
 * Interactive Dashboard — the Reports page as a drag-and-drop board.
 *
 * This is PURELY a different arrangement of the list view: same endpoints, same
 * transition rules, same confirmation dialogs. Dragging a card is just a
 * shortcut for pressing the button that card would have had in the list, so a
 * move made here is visible in the list view (and on the phone) as soon as
 * either refetches — the server is the only source of truth.
 *
 * Desktop + authority only; the parent decides that and simply doesn't render
 * this otherwise. The last column, Sent, is different in kind: those are
 * reports this authority filed themselves, so it is read-only tracking — no
 * drags in or out, and each card shows where the report stands with whoever
 * is handling it.
 */

const PAGE_SIZE = 15;

const BOARD_TABS: ReportTab[] = [...AUTHORITY_TABS, 'sent'];

type SentStatus = 'received' | 'in_progress' | 'closed';

const SENT_STATUS_STYLES: Record<SentStatus, { label: string; className: string }> = {
	received: { label: 'Received', className: 'bg-slate-100 text-slate-700' },
	in_progress: { label: 'In Progress', className: 'bg-amber-100 text-amber-700' },
	closed: { label: 'Closed', className: 'bg-emerald-100 text-emerald-700' },
};

type KanbanCard = {
	id: number;
	title: string;
	reportNumber: string | null;
	reporterName: string;
	createdAt: string;
	updatedAt: string | null;
	// Sent column only: who it went to, who holds it now, and its stage there.
	sentToName: string | null;
	nowWithName: string | null;
	sentStatus: SentStatus;
	closedAt: string | null;
	progressStartedAt: string | null;
	commentCount: number;
	forwardedFromName: string | null;
	latestForwardedToName: string | null;
	closedByName: string | null;
	closedByUserId: number | null;
};

type ColumnState = {
	cards: KanbanCard[];
	page: number;
	hasMore: boolean;
	isLoading: boolean;
	isFetchingMore: boolean;
};

const emptyColumn: ColumnState = {
	cards: [],
	page: 1,
	hasMore: true,
	isLoading: false,
	isFetchingMore: false,
};

const COLUMN_STYLES: Record<
	ReportTab,
	{ icon: JSX.Element; accent: string; ring: string; chip: string }
> = {
	received: {
		icon: <Download size={16} />,
		accent: 'bg-slate-500',
		ring: 'ring-slate-400',
		chip: 'bg-slate-100 text-slate-700',
	},
	progress: {
		icon: <Hourglass size={16} />,
		accent: 'bg-amber-500',
		ring: 'ring-amber-400',
		chip: 'bg-amber-100 text-amber-700',
	},
	active: {
		icon: <Send size={16} />,
		accent: 'bg-blue-500',
		ring: 'ring-blue-400',
		chip: 'bg-blue-100 text-blue-700',
	},
	closed: {
		icon: <XCircle size={16} />,
		accent: 'bg-emerald-500',
		ring: 'ring-emerald-400',
		chip: 'bg-emerald-100 text-emerald-700',
	},
	sent: {
		icon: <Upload size={16} />,
		accent: 'bg-gray-400',
		ring: 'ring-gray-300',
		chip: 'bg-gray-100 text-gray-600',
	},
};

const toCard = (raw: any): KanbanCard => ({
	id: raw.id,
	title: raw.title || 'Untitled Report',
	reportNumber: raw.reportNumber || null,
	reporterName: raw.isAnonymous ? 'Anonymous' : raw.user?.name || 'Unknown',
	createdAt: raw.createdAt,
	updatedAt: raw.updatedAt || null,
	sentToName: raw.originalAuthority?.name || raw.authority?.name || null,
	// Only worth saying when it's moved on from where it was sent.
	nowWithName:
		raw.currentAuthority && raw.currentAuthority.id !== raw.originalAuthority?.id
			? raw.currentAuthority.name
			: null,
	sentStatus: raw.isClosed ? 'closed' : raw.inProgress ? 'in_progress' : 'received',
	closedAt: raw.closedAt || null,
	progressStartedAt: raw.progressStartedAt || null,
	commentCount: raw.commentCount || 0,
	forwardedFromName: raw.forwardedFromAuthority?.name || null,
	latestForwardedToName: raw.latestForwardedToAuthority?.name || null,
	closedByName: raw.closedByAuthority?.name || null,
	closedByUserId: raw.closedByAuthority?.userId ?? null,
});

const DEFAULT_BOARD_SIDE_MARGIN = '24px';

interface ReportsKanbanProps {
	currentUserId?: number;
	currentAuthorityPhone?: number;
	currentUserName?: string;
	onOpenReport: (reportId: number) => void;
	/** Absolute margin (e.g. '24px' or 24) on both left and right sides of the page for the board */
	sideMargin?: string | number;
}

// The list ⇄ board switch is deliberately NOT here: the parent renders one
// fixed control for both views so it never moves when you switch.
const ReportsKanban: React.FC<ReportsKanbanProps> = ({
	currentUserId,
	currentAuthorityPhone,
	currentUserName,
	onOpenReport,
	sideMargin = DEFAULT_BOARD_SIDE_MARGIN,
}) => {
	const [columns, setColumns] = useState<Record<ReportTab, ColumnState>>({
		received: { ...emptyColumn },
		progress: { ...emptyColumn },
		active: { ...emptyColumn },
		closed: { ...emptyColumn },
		sent: { ...emptyColumn },
	});
	const [counts, setCounts] = useState<TabCounts>(EMPTY_COUNTS);
	const [searchQuery, setSearchQuery] = useState('');
	const activeSearchRef = useRef('');
	const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
	const [toast, setToast] = useState<string | null>(null);

	// Drag state. `dragOverTab` is only set for a LEGAL target, so the drop zone
	// highlight doubles as the rule feedback — an illegal column simply never
	// lights up.
	const [draggingFrom, setDraggingFrom] = useState<ReportTab | null>(null);
	const [draggingCard, setDraggingCard] = useState<KanbanCard | null>(null);
	const [dragOverTab, setDragOverTab] = useState<ReportTab | null>(null);

	// Press tracking, so a card opens on a CLICK but not at the end of a drag
	// gesture. This matters most on cards that can't be dragged at all (Forwarded,
	// and Closed ones somebody else closed): the browser fires a click when the
	// press ends on the same element, so trying to drag one used to open it.
	const press = useRef({ id: -1, x: 0, y: 0, moved: 0 });
	// The card the user is currently trying — and failing — to drag. Drives the
	// "not-allowed" cursor so the refusal is visible rather than just inert.
	const [blockedDragId, setBlockedDragId] = useState<number | null>(null);
	const DRAG_SLOP = 4; // px of movement before a press stops counting as a click

	// Pending confirmations. A drag never mutates anything on its own: it opens
	// the same dialog the equivalent button would, so the user gets one
	// consistent "are you sure" story across both views.
	const [pendingActivate, setPendingActivate] = useState<KanbanCard | null>(null);
	const [pendingClose, setPendingClose] = useState<KanbanCard | null>(null);
	const [pendingReopen, setPendingReopen] = useState<KanbanCard | null>(null);
	const [forwardCard, setForwardCard] = useState<KanbanCard | null>(null);

	// First visit to the board opens the guide by itself; after that it's one
	// click away in the header.
	const [showGuide, setShowGuide] = useState(false);
	useEffect(() => {
		if (currentUserId != null && !hasSeenKanbanGuide(currentUserId)) setShowGuide(true);
	}, [currentUserId]);
	const closeGuide = useCallback(() => {
		setShowGuide(false);
		if (currentUserId != null) markKanbanGuideSeen(currentUserId);
	}, [currentUserId]);
	useEffect(() => {
		if (!showGuide) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') closeGuide();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [showGuide, closeGuide]);

	useEffect(() => {
		if (!toast) return;
		const t = setTimeout(() => setToast(null), 3000);
		return () => clearTimeout(t);
	}, [toast]);

	// Safety net for the refusal cursor: clear it on ANY pointer release,
	// wherever it happens. The per-card pointerup/leave handlers cover the normal
	// paths, but a release that lands outside the card (dragged onto another
	// column, out of the window, interrupted by the OS) would otherwise leave the
	// card stuck showing "not-allowed" with no button held.
	useEffect(() => {
		const clear = () => setBlockedDragId(null);
		window.addEventListener('pointerup', clear);
		window.addEventListener('pointercancel', clear);
		window.addEventListener('blur', clear);
		return () => {
			window.removeEventListener('pointerup', clear);
			window.removeEventListener('pointercancel', clear);
			window.removeEventListener('blur', clear);
		};
	}, []);

	// Monotonic request ids per column so a slow page-1 response for an old
	// search term can't overwrite the results of a newer one.
	const reqIds = useRef<Record<string, number>>({});

	const fetchColumn = useCallback(async (tab: ReportTab, targetPage: number) => {
		setColumns((prev) => ({
			...prev,
			[tab]: {
				...prev[tab],
				isLoading: targetPage === 1,
				isFetchingMore: targetPage > 1,
			},
		}));
		const myReq = (reqIds.current[tab] = (reqIds.current[tab] || 0) + 1);
		try {
			const res = await fetch(
				buildReportsUrl(tab, targetPage, PAGE_SIZE, activeSearchRef.current),
				{ credentials: 'include' }
			);
			if (!res.ok) throw new Error('Failed to fetch reports');
			const raw = await res.json();
			if (myReq !== reqIds.current[tab]) return; // superseded
			if (!Array.isArray(raw)) throw new Error('Unexpected response');

			const cards = raw.map(toCard);
			setColumns((prev) => {
				const existing = targetPage === 1 ? [] : prev[tab].cards;
				const seen = new Set(existing.map((c) => c.id));
				return {
					...prev,
					[tab]: {
						cards: [...existing, ...cards.filter((c) => !seen.has(c.id))],
						page: targetPage,
						hasMore: raw.length >= PAGE_SIZE,
						isLoading: false,
						isFetchingMore: false,
					},
				};
			});
		} catch (err) {
			console.error(`Kanban ${tab} fetch failed:`, err);
			setColumns((prev) => ({
				...prev,
				[tab]: { ...prev[tab], isLoading: false, isFetchingMore: false },
			}));
		}
	}, []);

	const reloadAll = useCallback(async () => {
		BOARD_TABS.forEach((tab) => fetchColumn(tab, 1));
		const c = await fetchTabCounts(activeSearchRef.current);
		if (c) setCounts(c);
	}, [fetchColumn]);

	// Only the columns a move can touch need refetching, but reloading all of them
	// is a handful of small requests and removes any chance of one column going
	// stale after a multi-step move (activate then close, say).
	useEffect(() => {
		reloadAll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Debounced search, same 300 ms as the list view. Filtering happens on the
	// server so the columns page through matches only, and the header counts
	// describe the same filtered set.
	useEffect(() => {
		const effective = searchQuery.trim();
		if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
		searchDebounceRef.current = setTimeout(() => {
			if (effective === activeSearchRef.current) return;
			activeSearchRef.current = effective;
			reloadAll();
		}, 300);
		return () => {
			if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery]);

	// ── Mutations ────────────────────────────────────────────────────────────
	const runMutation = async (url: string, notFoundMsg: string) => {
		try {
			const res = await fetch(url, {
				method: 'PATCH',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
			});
			if (res.status === 404) {
				setToast(notFoundMsg);
				await reloadAll();
				return false;
			}
			if (!res.ok) {
				const err = await res.json().catch(() => ({}));
				throw new Error(err.message || 'Action failed');
			}
			await reloadAll();
			return true;
		} catch (err: any) {
			// The server owns the rules; if it rejects the move (someone else got
			// there first from another device), say why and resync the board.
			setToast(err?.message || 'Could not update this report.');
			await reloadAll();
			return false;
		}
	};

	const doActivate = async (card: KanbanCard) => {
		setPendingActivate(null);
		await runMutation(
			`${BACKEND_URL}/api/startReportProgress/${card.id}`,
			'This report no longer exists.'
		);
	};

	const doToggleClose = async (card: KanbanCard) => {
		setPendingClose(null);
		setPendingReopen(null);
		await runMutation(
			`${BACKEND_URL}/api/toggleReportClosed/${card.id}`,
			'This report no longer exists.'
		);
	};

	// ── Drag & drop ──────────────────────────────────────────────────────────
	const handleDragStart = (tab: ReportTab, card: KanbanCard) => {
		if (!isCardDraggable(tab, card.closedByUserId, currentUserId)) return;
		// A started drag must never also register as a click — see `press` below.
		press.current.moved = Number.POSITIVE_INFINITY;
		setDraggingFrom(tab);
		setDraggingCard(card);
	};

	const handleDragEnd = () => {
		setDraggingFrom(null);
		setDraggingCard(null);
		setDragOverTab(null);
	};

	const handleDrop = (to: ReportTab) => {
		const from = draggingFrom;
		const card = draggingCard;
		handleDragEnd();
		if (!from || !card || !isDropAllowed(from, to)) return;

		// Each legal move opens the SAME dialog its button counterpart uses.
		// Closed → In Progress is a reopen, not an activate.
		if (to === 'progress') {
			if (from === 'closed') setPendingReopen(card);
			else setPendingActivate(card);
		} else if (to === 'closed') setPendingClose(card);
		else if (to === 'active') setForwardCard(card);
	};

	// ── Rendering ────────────────────────────────────────────────────────────
	const relativeTime = (iso: string | null | undefined) => {
		if (!iso) return '';
		try {
			return formatCustomDistanceToNow(parseISO(iso));
		} catch {
			return '';
		}
	};

	const renderCard = (tab: ReportTab, card: KanbanCard) => {
		const draggable = isCardDraggable(tab, card.closedByUserId, currentUserId);
		const isBeingDragged = draggingCard?.id === card.id;
		const isBlocked = blockedDragId === card.id;

		// Which timestamp actually matters differs per column — "created 3d ago"
		// is noise in the Closed column when what you want is when it was closed.
		// Sent is ordered by latest activity, so that's the time it shows.
		const stamp =
			tab === 'closed'
				? relativeTime(card.closedAt) || relativeTime(card.createdAt)
				: tab === 'progress'
					? relativeTime(card.progressStartedAt) || relativeTime(card.createdAt)
					: tab === 'sent'
						? relativeTime(card.updatedAt) || relativeTime(card.createdAt)
						: relativeTime(card.createdAt);

		return (
			<div
				key={card.id}
				draggable={draggable}
				onDragStart={() => handleDragStart(tab, card)}
				onDragEnd={handleDragEnd}
				onPointerDown={(e) => {
					press.current = { id: card.id, x: e.clientX, y: e.clientY, moved: 0 };
				}}
				onPointerMove={(e) => {
					// No button held → this is a plain hover, not a drag attempt.
					//
					// This guard is what stops the refusal cursor sticking. The press
					// record deliberately survives pointerup (the click handler below
					// needs its distance to know the click ended a drag), so without
					// checking `buttons` a later hover over the same card would match the
					// stale record, re-arm the cursor, and leave it stuck until the
					// component remounted.
					if (e.buttons === 0) return;
					if (press.current.id !== card.id) return;
					press.current.moved = Math.max(
						press.current.moved,
						Math.hypot(e.clientX - press.current.x, e.clientY - press.current.y)
					);
					// Only undraggable cards need the explicit refusal: draggable ones
					// are already showing the browser's drag image.
					if (!draggable && press.current.moved > DRAG_SLOP && !isBlocked) {
						setBlockedDragId(card.id);
					}
				}}
				onPointerUp={() => setBlockedDragId(null)}
				onPointerCancel={() => setBlockedDragId(null)}
				onPointerLeave={() => setBlockedDragId(null)}
				onClick={() => {
					// Swallow the click that ends a drag gesture. Without this, trying to
					// drag a card that can't move would open the report on release.
					if (press.current.id === card.id && press.current.moved > DRAG_SLOP) {
						press.current.moved = 0;
						return;
					}
					onOpenReport(card.id);
				}}
				// select-none: dragging an undraggable card used to smear a text
				// selection across the board instead of doing nothing visible.
				className={`group select-none rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-all hover:border-gray-300 hover:shadow-md ${
					isBlocked
						? 'cursor-not-allowed'
						: draggable
							? 'cursor-grab active:cursor-grabbing'
							: 'cursor-pointer'
				} ${isBeingDragged ? 'opacity-40' : ''}`}
			>
				{/* Title line — the number stays put when the title truncates. */}
				<div className='flex items-baseline gap-1.5 min-w-0'>
					<h4 className='m-0 min-w-0 truncate text-[13px] font-semibold leading-snug text-gray-900'>
						{renderHighlightedText(card.title, searchQuery)}
					</h4>
					<ReportNumberChip number={card.reportNumber} searchQuery={searchQuery} />
				</div>

				{/* Meta line: reporter, when, and the comment count pushed to the right.
				    The count used to sit on a row of its own — it shared that row with
				    the Reopen button, and once that button went the row was a whole line
				    of whitespace for one number. */}
				<div className='mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-500'>
					{/* On Sent the reporter is me; who it went to is the useful name. */}
					<span className='truncate'>
						{tab === 'sent' ? `To ${card.sentToName || 'Unknown'}` : card.reporterName}
					</span>
					{stamp && (
						<>
							<span className='shrink-0 text-gray-300'>•</span>
							<Clock className='h-3 w-3 shrink-0' />
							<span className='truncate'>{stamp}</span>
						</>
					)}
					<span
						className='ml-auto inline-flex shrink-0 items-center gap-1 text-gray-400'
						title={`${card.commentCount} comment${card.commentCount === 1 ? '' : 's'}`}
					>
						<MessageCircle className='h-3 w-3' />
						{card.commentCount}
					</span>
				</div>

				{/* Chain context, only where it tells you something new. */}
				{tab === 'received' && card.forwardedFromName && (
					<div className='mt-2 truncate rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700'>
						From {card.forwardedFromName}
					</div>
				)}
				{tab === 'active' && card.latestForwardedToName && (
					<div className='mt-2 truncate rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700'>
						Forwarded to {card.latestForwardedToName}
					</div>
				)}
				{/* "Closed by X" appears ONLY when someone else closed it. That makes
				    the tag the at-a-glance answer to "can I move this card?": a Closed
				    card without it is one I closed, so I can drag it back to In
				    Progress to reopen. Tagging my own closures would just be telling me
				    my own name while hiding that distinction. */}
				{tab === 'closed' && card.closedByName && !draggable && (
					<div className='mt-2 truncate rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700'>
						Closed by {card.closedByName}
					</div>
				)}
				{/* Sent: where my report stands with whoever is handling it. */}
				{tab === 'sent' && (
					<div className='mt-2 flex min-w-0 items-center gap-1.5'>
						<span
							className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${SENT_STATUS_STYLES[card.sentStatus].className}`}
						>
							{SENT_STATUS_STYLES[card.sentStatus].label}
						</span>
						{card.sentStatus === 'closed' && card.closedByName ? (
							<span className='truncate text-[10px] text-gray-500'>by {card.closedByName}</span>
						) : (
							card.nowWithName && (
								<span className='truncate text-[10px] text-blue-700' title={`Forwarded to ${card.nowWithName}`}>
									Now with {card.nowWithName}
								</span>
							)
						)}
					</div>
				)}

				{renderCardActions(tab, card, draggable)}
			</div>
		);
	};

	// Button equivalents of every legal drag, for anyone who doesn't drag (or
	// doesn't know they can). Each one opens exactly the dialog its drop would.
	const renderCardActions = (tab: ReportTab, card: KanbanCard, draggable: boolean) => {
		const actions: { label: string; icon: JSX.Element; className: string; onClick: () => void }[] = [];
		if (canActivate(tab)) {
			actions.push({
				label: 'Activate',
				icon: <PlayCircle className='h-3 w-3' />,
				className: 'bg-amber-500 text-white hover:bg-amber-600',
				onClick: () => setPendingActivate(card),
			});
		}
		if (canCloseFrom(tab)) {
			actions.push({
				label: 'Close',
				icon: <XCircle className='h-3 w-3' />,
				className: 'bg-red-600 text-white hover:bg-red-700',
				onClick: () => setPendingClose(card),
			});
		}
		// `draggable` on a Closed card means I closed it, i.e. I may reopen it.
		if (tab === 'closed' && draggable) {
			actions.push({
				label: 'Reopen',
				icon: <RotateCcw className='h-3 w-3' />,
				className: 'bg-green-600 text-white hover:bg-green-700',
				onClick: () => setPendingReopen(card),
			});
		}
		if (canForwardFrom(tab)) {
			actions.push({
				label: 'Forward',
				icon: <Send className='h-3 w-3' />,
				className: 'border border-blue-200 bg-white text-blue-600 hover:bg-blue-50',
				onClick: () => setForwardCard(card),
			});
		}
		if (actions.length === 0) return null;

		return (
			<div className='mt-2.5 flex gap-1.5 border-t border-gray-100 pt-2'>
				{actions.map((a) => (
					<button
						key={a.label}
						type='button'
						onClick={(e) => {
							// The card itself opens the report on click.
							e.stopPropagation();
							a.onClick();
						}}
						className={`inline-flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ${a.className}`}
					>
						{a.icon}
						{a.label}
					</button>
				))}
			</div>
		);
	};

	const renderColumn = (tab: ReportTab) => {
		const col = columns[tab];
		const style = COLUMN_STYLES[tab];
		const isTarget = dragOverTab === tab;
		const legalTarget = draggingFrom ? isDropAllowed(draggingFrom, tab) : false;

		return (
			<div
				key={tab}
				className={`flex min-h-0 flex-1 flex-col rounded-xl border bg-gray-50 transition-colors ${isTarget ? `border-transparent ring-2 ${style.ring} bg-white` : 'border-gray-200'
					} ${draggingFrom && !legalTarget && draggingFrom !== tab ? 'opacity-50' : ''}`}
				onDragOver={(e) => {
					// Only a legal target may accept the drop — preventDefault is what
					// makes a zone droppable, so withholding it on an illegal column
					// gives the "not allowed" cursor for free.
					if (!draggingFrom || !isDropAllowed(draggingFrom, tab)) return;
					e.preventDefault();
					if (dragOverTab !== tab) setDragOverTab(tab);
				}}
				onDragLeave={(e) => {
					if (e.currentTarget.contains(e.relatedTarget as Node)) return;
					if (dragOverTab === tab) setDragOverTab(null);
				}}
				onDrop={(e) => {
					e.preventDefault();
					handleDrop(tab);
				}}
			>
				<div className='flex items-center gap-2 border-b border-gray-200 px-3 py-2.5'>
					<span className={`h-2 w-2 shrink-0 rounded-full ${style.accent}`} />
					<span className='text-gray-500'>{style.icon}</span>
					<span className='text-sm font-bold text-gray-800'>{TAB_LABELS[tab]}</span>
					<span
						className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-bold ${style.chip}`}
					>
						{countForTab(counts, tab)}
					</span>
				</div>

				<div
					className='flex-1 space-y-2 overflow-y-auto p-2'
					onScroll={(e) => {
						// Per-column infinite scroll: each column pages independently, so a
						// long Closed column never forces the others to load too.
						const el = e.currentTarget;
						if (
							col.hasMore &&
							!col.isFetchingMore &&
							!col.isLoading &&
							el.scrollTop + el.clientHeight >= el.scrollHeight - 200
						) {
							fetchColumn(tab, col.page + 1);
						}
					}}
				>
					{col.isLoading && col.cards.length === 0 ? (
						<div className='py-8 text-center text-xs text-gray-400'>Loading…</div>
					) : col.cards.length === 0 ? (
						<div className='py-8 text-center text-xs text-gray-400'>
							{activeSearchRef.current ? 'No matches' : 'Nothing here'}
						</div>
					) : (
						col.cards.map((card) => renderCard(tab, card))
					)}
					{col.isFetchingMore && (
						<div className='py-3 text-center text-xs text-gray-400'>Loading more…</div>
					)}
				</div>
			</div>
		);
	};

	const ConfirmDialog = ({
		icon,
		iconBg,
		title,
		body,
		confirmLabel,
		confirmClass,
		onCancel,
		onConfirm,
	}: {
		icon: JSX.Element;
		iconBg: string;
		title: string;
		body: React.ReactNode;
		confirmLabel: string;
		confirmClass: string;
		onCancel: () => void;
		onConfirm: () => void;
	}) => (
		<div
			className='fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm'
			onClick={onCancel}
		>
			<div
				className='w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl'
				onClick={(e) => e.stopPropagation()}
			>
				<div className='p-6 text-center'>
					<div
						className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${iconBg}`}
					>
						{icon}
					</div>
					<h3 className='mb-2 text-xl font-bold text-gray-900'>{title}</h3>
					<p className='mb-6 text-sm leading-relaxed text-gray-600'>{body}</p>
					<div className='flex gap-3'>
						<button
							onClick={onCancel}
							className='flex-1 rounded-xl bg-gray-100 px-4 py-2.5 font-semibold text-gray-700 transition-colors hover:bg-gray-200'
						>
							Cancel
						</button>
						<button
							onClick={onConfirm}
							className={`flex-1 rounded-xl px-4 py-2.5 font-semibold text-white transition-colors ${confirmClass}`}
						>
							{confirmLabel}
						</button>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<div className='py-6'>
			{toast && (
				<div className='fixed left-1/2 top-6 z-[1100] -translate-x-1/2 rounded-xl bg-gray-900 px-5 py-3 text-sm font-semibold text-white shadow-2xl'>
					{toast}
				</div>
			)}

			{/* Header: title block positioned same as list view (50% - 312px, text-left), search right. */}
			<div
				className='relative mb-6 flex min-h-[52px] flex-col gap-4 md:flex-row'
				style={{
					paddingLeft: typeof sideMargin === 'number' ? `${sideMargin}px` : sideMargin,
					paddingRight: typeof sideMargin === 'number' ? `${sideMargin}px` : sideMargin,
					['--side-margin' as any]: typeof sideMargin === 'number' ? `${sideMargin}px` : sideMargin,
				}}
			>
				<div className='w-full min-w-0 text-left md:absolute md:left-[calc(50%-312px)] md:top-0 md:w-[624px] md:max-w-[624px]'>
					<h1 className='m-0 text-2xl font-bold leading-tight text-gray-900 text-left'>
						Hello {currentUserName || 'User'},
					</h1>
					<p className='m-0 mt-1 leading-tight text-gray-600 text-left'>
						Drag a report, or use its buttons, to move it between stages.{' '}
						<button
							type='button'
							onClick={() => setShowGuide(true)}
							className='inline-flex items-center gap-1 font-semibold text-blue-600 hover:text-blue-700 hover:underline'
						>
							<HelpCircle className='h-3.5 w-3.5' />
							How it works
						</button>
					</p>
				</div>

				<div className='relative w-full max-w-sm shrink-0 md:absolute md:left-[calc(50%+312px)] md:right-[var(--side-margin)] md:top-0 md:w-auto md:max-w-none'>
					<Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
					<input
						type='text'
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						placeholder='Search by Title or Report number'
						className='h-10 w-full rounded-full border border-gray-200 bg-white pl-9 pr-16 text-sm text-gray-900 shadow-sm transition-shadow hover:shadow-md focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
					/>
					{searchQuery && (
						<button
							onClick={() => setSearchQuery('')}
							className='absolute right-1.5 top-1/2 flex h-7 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-gray-100 px-3 text-[10px] font-bold leading-none text-gray-500 transition-colors hover:bg-gray-200'
						>
							Clear
						</button>
					)}
				</div>
			</div>

			{/* Fixed-height board so each column scrolls on its own rather than the
			    page growing to the tallest one. `sideMargin` dictates equal left and
			    right margins from the page edges, allowing the board to flow under
			    the fixed view-switch button. */}
			<div
				className='flex gap-3'
				style={{
					height: 'calc(100vh - var(--header-height, 80px) - 130px)',
					paddingLeft: typeof sideMargin === 'number' ? `${sideMargin}px` : sideMargin,
					paddingRight: typeof sideMargin === 'number' ? `${sideMargin}px` : sideMargin,
				}}
			>
				{BOARD_TABS.map((tab) => renderColumn(tab))}
			</div>

			{pendingActivate && (
				<ConfirmDialog
					icon={<PlayCircle className='h-8 w-8 text-amber-600' />}
					iconBg='bg-amber-100'
					title='Start working on this report?'
					body={
						<>
							This moves the report to <strong>In Progress</strong> and notifies the reporter that
							you've started working on it.
							<br />
							You'll be able to close it once it's in progress.
						</>
					}
					confirmLabel='Activate'
					confirmClass='bg-amber-500 hover:bg-amber-600 shadow-lg shadow-amber-200'
					onCancel={() => setPendingActivate(null)}
					onConfirm={() => doActivate(pendingActivate)}
				/>
			)}

			{pendingClose && (
				<ConfirmDialog
					icon={<XCircle className='h-8 w-8 text-red-600' />}
					iconBg='bg-red-100'
					title='Close Report?'
					body={
						<>
							Are you sure you want to close this report? The reporter and other authorities
							involved will be able to see that you closed it.
							<br />
							You can reopen the report anytime.
						</>
					}
					confirmLabel='Close'
					confirmClass='bg-red-600 hover:bg-red-700 shadow-lg shadow-red-200'
					onCancel={() => setPendingClose(null)}
					onConfirm={() => doToggleClose(pendingClose)}
				/>
			)}

			{pendingReopen && (
				<ConfirmDialog
					icon={<RotateCcw className='h-8 w-8 text-green-600' />}
					iconBg='bg-green-100'
					title='Reopen Report?'
					body='Are you sure you want to reopen this report? It will move back to In Progress for you.'
					confirmLabel='Reopen'
					confirmClass='bg-green-600 hover:bg-green-700 shadow-lg shadow-green-200'
					onCancel={() => setPendingReopen(null)}
					onConfirm={() => doToggleClose(pendingReopen)}
				/>
			)}

			{showGuide && <KanbanGuide onClose={closeGuide} />}

			<ForwardReportModal
				isOpen={!!forwardCard}
				onClose={() => setForwardCard(null)}
				reportId={forwardCard?.id ?? null}
				currentAuthorityPhone={currentAuthorityPhone}
				currentUserId={currentUserId}
				onForwardSuccess={() => {
					setForwardCard(null);
					reloadAll();
				}}
			/>
		</div>
	);
};

export default ReportsKanban;
